/**
 * useOpenHouses.js — Hooks for the Open House Planner (/openhouse)
 *
 * Tables: open_houses, oh_plans, oh_plan_stops (generic CRUD)
 * Special endpoints: POST /openhouse/refresh (scrape), POST /openhouse/route
 * (time-window route optimizer), POST /ai/parse-open-house (paste import).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel, camelToSnake } from '../lib/utils';

function mapFromDb(row) { return snakeToCamel(row); }
function mapToDb(obj) { return camelToSnake(obj); }

// ── open_houses ─────────────────────────────────────────────────────────

export function useOpenHouses() {
  return useQuery({
    queryKey: queryKeys.openHouses.all,
    queryFn: async () => {
      const data = await api.get('/open-houses');
      return data.map(mapFromDb);
    },
  });
}

export function useCreateOpenHouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (oh) => mapFromDb(await api.post('/open-houses', mapToDb(oh))),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.openHouses.all }),
  });
}

export function useUpdateOpenHouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }) => mapFromDb(await api.put(`/open-houses/${id}`, mapToDb(updates))),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.openHouses.all }),
  });
}

export function useDeleteOpenHouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => { await api.del(`/open-houses/${id}`); return id; },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.openHouses.all }),
  });
}

/** Trigger the Redfin + Village Properties scrape. Returns counts. */
export function useRefreshOpenHouses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => api.post('/openhouse/refresh', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.openHouses.all }),
  });
}

/** Trigger Zillow enrichment (agent/brokerage/photos) — runs in background. */
export function useEnrichZillow() {
  return useMutation({
    mutationFn: async () => api.post('/openhouse/enrich-zillow', {}),
  });
}

/** Poll Zillow enrichment status. Refetches while running so the UI can show progress. */
export function useZillowStatus() {
  return useQuery({
    queryKey: ['openhouse', 'zillow-status'],
    queryFn: async () => api.get('/openhouse/enrich-zillow/status'),
    refetchInterval: (query) => (query.state.data?.running ? 5000 : false),
    staleTime: 0,
  });
}

/**
 * AI paste-import: raw text from Zillow/Redfin/brokerage pages →
 * { entries: [{address, city, zip, price, beds, baths, sqft, date,
 *   startTime, endTime, agentName, agentPhone, brokerage, listingUrl}] }
 */
export function useParseOpenHousePaste() {
  return useMutation({
    mutationFn: async (text) => api.post('/ai/parse-open-house', { text }),
  });
}

// ── oh_plans ────────────────────────────────────────────────────────────

export function useOhPlans() {
  return useQuery({
    queryKey: queryKeys.ohPlans.all,
    queryFn: async () => {
      const data = await api.get('/oh-plans');
      return data.map(mapFromDb);
    },
  });
}

export function useCreateOhPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (plan) => mapFromDb(await api.post('/oh-plans', mapToDb(plan))),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.ohPlans.all }),
  });
}

export function useUpdateOhPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }) => mapFromDb(await api.put(`/oh-plans/${id}`, mapToDb(updates))),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.ohPlans.all }),
  });
}

export function useDeleteOhPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => { await api.del(`/oh-plans/${id}`); return id; },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.ohPlans.all });
      qc.invalidateQueries({ queryKey: queryKeys.ohPlanStops.all });
    },
  });
}

// ── oh_plan_stops ───────────────────────────────────────────────────────

export function useOhPlanStops(planId) {
  return useQuery({
    queryKey: queryKeys.ohPlanStops.byPlan(planId),
    queryFn: async () => {
      const data = await api.get(`/oh-plan-stops?plan_id=${planId}`);
      return data.map(mapFromDb);
    },
    enabled: !!planId,
  });
}

export function useAllOhPlanStops() {
  return useQuery({
    queryKey: queryKeys.ohPlanStops.all,
    queryFn: async () => {
      const data = await api.get('/oh-plan-stops');
      return data.map(mapFromDb);
    },
  });
}

export function useCreateOhPlanStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (stop) => mapFromDb(await api.post('/oh-plan-stops', mapToDb(stop))),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.ohPlanStops.all }),
  });
}

export function useUpdateOhPlanStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }) => mapFromDb(await api.put(`/oh-plan-stops/${id}`, mapToDb(updates))),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.ohPlanStops.all }),
  });
}

export function useDeleteOhPlanStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => { await api.del(`/oh-plan-stops/${id}`); return id; },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.ohPlanStops.all }),
  });
}

// ── Route optimizer ─────────────────────────────────────────────────────

/**
 * POST /openhouse/route
 * body: { origin: {lat, lng} | {address}, departAt: ISO, dwellMin: number,
 *         stops: [{id, lat, lng, windowStart: ISO, windowEnd: ISO}] }
 * returns: { feasible, order: [stopId], schedule: [{id, arriveAt, departAt,
 *            driveMinFromPrev, waitMin, withinWindow}], droppedStops: [stopId],
 *            totalDriveMin, finishAt, path: [{lat, lng}], warnings: [string] }
 */
export function useOptimizeRoute() {
  return useMutation({
    mutationFn: async (payload) => api.post('/openhouse/route', payload),
  });
}
